import {
  DopamineHistoryAPIMessage,
  DopamineHistoryMessage,
  DopamineHistoryToolCall,
  DopamineSessionHistory,
  DopamineSessionSummary,
  DopamineSessionHistoryResponse,
  DopamineSessionsResponse,
} from 'interfaces'
import axios from 'axios'
import {unwrapAPIError} from 'utils'
import Logger from 'services/Logger'
import {getDopamineAssistantBaseUrl, requestHandler} from '../HTTP'
import {HttpMethods} from '../HTTP/HTTP.types'
import {streamChatRequest, StreamChatOptions} from './streamChat'

class DopamineAssistantRequestError extends Error {
  readonly status: number | undefined

  constructor(message: string, status: number | undefined) {
    super(message)
    this.name = 'DopamineAssistantRequestError'
    this.status = status
  }
}

export const isDopamineAssistantNotFoundError = (error: unknown): boolean =>
  error instanceof DopamineAssistantRequestError && error.status === 404

const getRequiredBaseUrl = (): string => {
  const configuredBaseUrl = getDopamineAssistantBaseUrl().trim()
  let endIndex = configuredBaseUrl.length

  while (endIndex > 0 && configuredBaseUrl[endIndex - 1] === '/') {
    endIndex -= 1
  }

  const baseUrl = configuredBaseUrl.slice(0, endIndex)

  if (!baseUrl) {
    throw new Error('Dopamine Assistant API URL is not configured')
  }

  return baseUrl
}

const isHistoryToolCall = (
  value: unknown,
): value is DopamineHistoryToolCall => {
  if (typeof value !== 'object' || value === null) return false

  const toolCall = value as Partial<DopamineHistoryToolCall>
  return (
    typeof toolCall.displayName === 'string' &&
    typeof toolCall.id === 'string' &&
    toolCall.type === 'function'
  )
}

const isHistoryAPIMessage = (
  value: unknown,
): value is DopamineHistoryAPIMessage => {
  if (typeof value !== 'object' || value === null) return false

  const message = value as {
    content?: unknown
    id?: unknown
    role?: unknown
    toolCallId?: unknown
    toolCalls?: unknown
  }
  if (typeof message.id !== 'string') return false

  if (message.role === 'user') return typeof message.content === 'string'
  if (message.role === 'tool') return typeof message.toolCallId === 'string'
  if (message.role !== 'assistant' || typeof message.content !== 'string') {
    return false
  }

  return (
    message.toolCalls === undefined ||
    (Array.isArray(message.toolCalls) &&
      message.toolCalls.every(isHistoryToolCall))
  )
}

const isSessionHistoryResponse = (
  value: unknown,
  expectedSessionId: string,
): value is DopamineSessionHistoryResponse => {
  if (typeof value !== 'object' || value === null) return false

  const response = value as Partial<DopamineSessionHistoryResponse>
  const history = response.data
  return (
    typeof history === 'object' &&
    history !== null &&
    history.sessionId === expectedSessionId &&
    Array.isArray(history.messages) &&
    history.messages.every(isHistoryAPIMessage)
  )
}

const toHistoryMessages = (
  messages: DopamineHistoryAPIMessage[],
): DopamineHistoryMessage[] =>
  messages.flatMap(message => {
    if (message.role === 'tool') return []
    if (message.role === 'user') return [message]

    const toolCalls: DopamineHistoryMessage[] = (message.toolCalls ?? []).map(
      toolCall => ({
        content: toolCall.displayName,
        id: toolCall.id,
        role: 'tool_call',
      }),
    )
    const assistantMessage: DopamineHistoryMessage = {
      content: message.content,
      id: message.id,
      role: message.role,
    }
    return message.content ? [assistantMessage, ...toolCalls] : toolCalls
  })

const isSessionSummary = (value: unknown): value is DopamineSessionSummary => {
  if (typeof value !== 'object' || value === null) return false

  const session = value as Partial<DopamineSessionSummary>
  return (
    typeof session.sessionId === 'string' &&
    session.sessionId.trim().length > 0 &&
    typeof session.title === 'string' &&
    typeof session.createdAt === 'string' &&
    Number.isFinite(Date.parse(session.createdAt))
  )
}

const isSessionsResponseData = (
  value: unknown,
): value is DopamineSessionsResponse['data'] => {
  if (typeof value !== 'object' || value === null) return false

  const responseData = value as Partial<DopamineSessionsResponse['data']>
  return (
    Array.isArray(responseData.sessions) &&
    responseData.sessions.every(isSessionSummary)
  )
}

const getSessions = async (): Promise<DopamineSessionSummary[]> => {
  try {
    const baseUrl = getRequiredBaseUrl()
    const {data} = await requestHandler<DopamineSessionsResponse>({
      method: HttpMethods.GET,
      options: {timeout: 30000},
      url: `${baseUrl}/sessions`,
    })
    if (!isSessionsResponseData(data?.data)) {
      throw new Error('Invalid Dopamine Assistant sessions response')
    }
    return data.data.sessions
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getSessionHistory = async (
  sessionId: string,
): Promise<DopamineSessionHistory> => {
  try {
    const baseUrl = getRequiredBaseUrl()
    const {data} = await requestHandler<DopamineSessionHistoryResponse>({
      method: HttpMethods.GET,
      options: {timeout: 30000},
      url: `${baseUrl}/sessions/${encodeURIComponent(sessionId)}`,
    })
    if (!isSessionHistoryResponse(data, sessionId)) {
      throw new Error('Invalid Dopamine Assistant session history response')
    }
    return {
      messages: toHistoryMessages(data.data.messages),
      sessionId: data.data.sessionId,
    }
  } catch (error) {
    Logger.LogNonAxiosError(error)
    if (axios.isAxiosError(error)) {
      throw new DopamineAssistantRequestError(
        unwrapAPIError(error),
        error.response?.status,
      )
    }
    throw unwrapAPIError(error)
  }
}

const streamChat = async (options: StreamChatOptions): Promise<void> => {
  try {
    await streamChatRequest({...options, baseUrl: getRequiredBaseUrl()})
  } catch (error) {
    if (!options.signal.aborted) Logger.LogNonAxiosError(error)
    throw error
  }
}

export const DopamineAssistantService = {
  getSessions,
  getSessionHistory,
  streamChat,
}
